import { useRef, useState } from 'react';
import { syntheticDemoPortalsEnabled } from '../../../shared/config/api-origin';
import type { ProductDraftErrors, ProductDraftFields } from '../domain/product-form';
import { cloneProductFields, emptyProductDraft } from '../domain/product-form';
import type { ProductDemoDraft } from '../fixtures/product-submission-demo';
import {
  createProductDemoSeeds,
  deleteProductDemoDraft,
  isDemoProductDraftId,
  productFieldsFromFixture,
  saveProductDemoDraft,
} from '../fixtures/product-submission-demo';
import type { ProviderProduct } from '../fixtures/provider-data';
import './product-submission-prototype.css';
import { ProductModelEditor } from './ProductModelEditor';

/**
 * What the editor is editing. `id` is deliberately a plain string: the DEMO path passes a
 * `demo-product-…` draft id, and the SYN LIVE path passes the durable 06 product id. Typing it as
 * the demo brand would have forced the LIVE path to invent a demo-shaped identifier for a real one.
 */
export interface ProductEditorContext {
  id: string;
  fields: ProductDraftFields;
  baseline: ProductDraftFields | null;
  fixtureId: string | null;
}
export function useProductSubmissionSession() {
  const [records, setRecords] = useState<readonly ProductDemoDraft[]>(() =>
    syntheticDemoPortalsEnabled() ? createProductDemoSeeds() : [],
  );
  const [editor, setEditor] = useState<ProductEditorContext | null>(null);
  const [message, setMessage] = useState('');
  const [operationErrors, setOperationErrors] = useState<ProductDraftErrors>({});
  const [filters, setFilters] = useState({ query: '', category: '全部品类', status: '全部' });
  const [submissionFilter, setSubmissionFilter] = useState('全部');
  const sequence = useRef(0);
  const opener = useRef<HTMLElement | null>(null);
  const begin = (context: ProductEditorContext) => {
    if (!syntheticDemoPortalsEnabled()) return;
    opener.current =
      typeof document === 'undefined' ? null : (document.activeElement as HTMLElement);
    setMessage('');
    setOperationErrors({});
    setEditor(context);
  };
  const openNew = () =>
    begin({
      id: `demo-product-session-${++sequence.current}`,
      fields: emptyProductDraft(),
      baseline: null,
      fixtureId: null,
    });
  const openFixture = (fixture: ProviderProduct) => {
    const existing = records.find((record) => record.baselineFixtureId === fixture.id);
    if (existing) {
      openDraft(existing);
      return;
    }
    const baseline = productFieldsFromFixture(fixture);
    begin({
      id: `demo-product-revision-${++sequence.current}`,
      fields: cloneProductFields(baseline),
      baseline,
      fixtureId: fixture.id,
    });
  };
  const openDraft = (record: ProductDemoDraft) => {
    if (record.status === 'PENDING_REVIEW') {
      setMessage('待审核演示资料只读，不能重复提交或编辑。');
      return;
    }
    begin({
      id: record.demoProductDraftId,
      fields: cloneProductFields(record.fields),
      baseline: record.baseline ? cloneProductFields(record.baseline) : null,
      fixtureId: record.baselineFixtureId,
    });
  };
  const closeEditor = () => {
    const returnFocus = opener.current;
    setEditor(null);
    if (typeof requestAnimationFrame !== 'undefined')
      requestAnimationFrame(() => {
        if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      });
  };
  const save = (fields: ProductDraftFields, submitting: boolean): ProductDraftErrors => {
    if (!syntheticDemoPortalsEnabled() || !editor) return { state: 'DEV 原型当前不可用' };
    if (!isDemoProductDraftId(editor.id)) return { state: 'DEV 原型当前不可用' };
    const result = saveProductDemoDraft(
      records,
      editor.id,
      fields,
      editor.baseline,
      editor.fixtureId,
      submitting,
    );
    if (!Object.keys(result.errors).length) {
      setRecords(result.records);
      setMessage(result.message);
      setOperationErrors({});
      closeEditor();
    }
    return result.errors;
  };
  const submit = (record: ProductDemoDraft) => {
    if (!syntheticDemoPortalsEnabled()) return;
    const result = saveProductDemoDraft(
      records,
      record.demoProductDraftId,
      record.fields,
      record.baseline,
      record.baselineFixtureId,
      true,
    );
    setRecords(result.records);
    setOperationErrors(result.errors);
    setMessage(result.message);
  };
  const remove = (record: ProductDemoDraft) => {
    if (!syntheticDemoPortalsEnabled()) return;
    const result = deleteProductDemoDraft(records, record.demoProductDraftId);
    setRecords(result.records);
    setOperationErrors(result.errors);
    setMessage(result.message);
  };
  return {
    records,
    editor,
    message,
    operationErrors,
    filters,
    setFilters,
    submissionFilter,
    setSubmissionFilter,
    openNew,
    openFixture,
    openDraft,
    closeEditor,
    save,
    submit,
    remove,
  };
}
export type ProductSubmissionSession = ReturnType<typeof useProductSubmissionSession>;
export function ProductSubmissionPrototype({ session }: { session: ProductSubmissionSession }) {
  if (!syntheticDemoPortalsEnabled()) return null;
  const local = session.records.filter((record) => record.origin === 'SESSION');
  return (
    <>
      <section className="product-demo-toolbar" aria-label="产品资料本地演示工具">
        <div>
          <strong>厂商产品资料 · DEV 流程演示</strong>
          <p>无真实厂商身份 / 服务器保存 · 刷新清空会话草稿 · 不发送到 08</p>
          <span>
            本次会话：{local.length} 条 · 草稿 {local.filter((r) => r.status === 'DRAFT').length} ·
            待审核 {local.filter((r) => r.status === 'PENDING_REVIEW').length}
          </span>
        </div>
        <button className="portal-button" onClick={session.openNew}>
          新增产品（演示）
        </button>
      </section>
      {session.message && (
        <p role="status" className="product-demo-notice">
          {session.message}
        </p>
      )}
      {Object.keys(session.operationErrors).length > 0 && (
        <div role="alert" className="product-demo-errors">
          {Object.values(session.operationErrors).map((error, index) => (
            <p key={index}>{error}</p>
          ))}
          <p>请打开草稿修订后再模拟提交。</p>
        </div>
      )}
      {session.editor && (
        <ProductModelEditor
          key={session.editor.id}
          context={session.editor}
          records={session.records}
          onClose={session.closeEditor}
          onSave={session.save}
        />
      )}
    </>
  );
}
